import { NgClass } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, Output, ChangeDetectionStrategy, OnDestroy } from '@angular/core';
import { FontAwesomeModule, IconDefinition } from '@fortawesome/angular-fontawesome';
import { faStop, faPlay, faXmark, faMicrophone, faPause } from '@fortawesome/free-solid-svg-icons';
import { FormsModule } from '@angular/forms';
import { EventEmitter } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { TooltipDirective } from '../../directives/tooltip.directive';

@Component({
  selector: 'app-audio',
  templateUrl: './audio.component.html',
  styleUrls: ['./audio.component.sass'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [NgClass, FontAwesomeModule, FormsModule, TooltipDirective]
})
export class AudioComponent implements OnDestroy {
  public faCircleIcn: IconDefinition = faMicrophone;
  public faStopIcn: IconDefinition = faStop;
  public faPlayIcn: IconDefinition = faPlay;
  public faXmarkIcn: IconDefinition = faXmark;
  public faPauseIcn: IconDefinition = faPause; // Add the pause icon
  isRecording = false;
  isPlaying = false; // New property to track play/pause state
  audio: HTMLAudioElement | null = null; // Store the audio instance


  audioUrl: string | null = null;
  recordingSizeMB: number | null = null; // New property to store the size in MB
  recordingTime = 0; // Timer in seconds
  private mediaRecorder: MediaRecorder | null = null;
  private audioChunks: Blob[] = [];
  private audioBlob: Blob | null = null;
  private recordingTimeout: any = null;
  private timerInterval: any = null; // Interval for the timer

  public mimeType: string = 'webm';

  @Output() record: EventEmitter<{blob:string, duration:number, size:number, type:string}> = new EventEmitter();

  constructor(private http: HttpClient, private toastr: ToastrService) {}

  async startRecording() {
    this.stopPlayback();
    //this.toastr.info('Starting recording...');
    if (typeof MediaRecorder === 'undefined') {
      this.toastr.error('Audio recording is not supported in this browser. Please use Chrome or Firefox.');
      return;
    }
    try {
      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      let mimeType = '';
      if (MediaRecorder.isTypeSupported('audio/mp4')) {
        mimeType = 'audio/mp4';
        this.mimeType = 'mp4';
      } else if (MediaRecorder.isTypeSupported('audio/mp3')) {
        mimeType = 'audio/mp3';
        this.mimeType = 'mp3';
      } else if (MediaRecorder.isTypeSupported('audio/webm')) {
        mimeType = 'audio/webm';
        this.mimeType = 'webm';
      }

      this.mediaRecorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);

    //this.toastr.info(this.mimeType +' ' +this.mediaRecorder.toString());

      // Collect audio data chunks
      this.mediaRecorder.ondataavailable = (event) => {
        this.audioChunks.push(event.data);
      };

      // Start recording
      this.mediaRecorder.start();
      this.isRecording = true;
      this.recordingTime = 0; // Reset the timer

      // Start the timer
      this.timerInterval = setInterval(() => {
        this.recordingTime++;
      }, 1000);

      // Set a timeout to stop recording after 15 seconds
      this.recordingTimeout = setTimeout(() => {
        this.stopRecording();
        alert('Recording stopped automatically after 15 seconds.');
      }, 15000); // 15 seconds
    } catch (error) {
      console.error('Error accessing microphone:', error);
      alert('Microphone access is required to record audio.');
    }
  }

  resetRecording(){
    this.stopPlayback();
    this.recordingSizeMB = null;
    this.audioUrl='';
    this.record.emit({blob:'', duration:0, size:0, type:''});
  }

  stopRecording() {
    this.recordingTime = 0;

    //this.toastr.info('Stopping recording...');
    //this.toastr.error(this.mediaRecorder.toString());

    this.isRecording = false;
    if (this.mediaRecorder) {
      this.recordingSizeMB = 0.1;
      this.mediaRecorder.stop();
      this.mediaRecorder.onstop = () => {
        // Combine audio chunks into a single Blob
        this.audioBlob = new Blob(this.audioChunks, { type: 'audio/'+this.mimeType });
        this.audioUrl = URL.createObjectURL(this.audioBlob);
        this.audioChunks = []; // Clear chunks for the next recording

        // Calculate the size of the recording in MB
        if (this.audioBlob) {
          const blobSize = this.audioBlob.size;
          this.recordingSizeMB = (blobSize / (1024 * 1024)).toFixed(2) as unknown as number;

          const reader = new FileReader();
          reader.readAsDataURL(this.audioBlob);
          reader.onloadend = () => {
            const base64data = reader.result as string; // Base64-encoded string

            const payload = {
              fileName: 'recording.'+this.mimeType,
              fileData: base64data.split(',')[1] // Remove the Base64 prefix
            };
            this.record.emit({blob:payload.fileData, duration:this.recordingTime, size:blobSize,type:'audio/'+this.mimeType});

          }    

        }
      };

      // Clear the timer
      if (this.timerInterval) {
        clearInterval(this.timerInterval);
        this.timerInterval = null;
      }

      // Clear the timeout if the recording is stopped manually
      if (this.recordingTimeout) {
        clearTimeout(this.recordingTimeout);
        this.recordingTimeout = null;
      }
    }else{
      this.recordingSizeMB = null;
    }
  }

  pauseAudio() {
    if (this.audio) {
      this.audio.pause();
    }
    this.isPlaying = false;
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  private stopPlayback() {
    this.pauseAudio();
    this.audio = null;
    this.recordingTime = 0;
  }

  ngOnDestroy() {
    this.stopPlayback();
  }

  playAudio(fromStart: boolean = false) {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }

    //this.toastr.info('playAudio' +this.audioUrl);

    if (this.audioUrl) {
      if(!this.audio){
        this.audio = new Audio(this.audioUrl);
        this.recordingTime = 0;
      }
      if (fromStart) {
        this.audio.pause();
        this.audio.currentTime = 0;
        this.recordingTime = 0;
      }
      this.isPlaying = true;
      // Start the timer while the audio is playing
      this.timerInterval = setInterval(() => {
        this.recordingTime++;
      }, 1000);

      // Stop the timer when the audio finishes playing
      this.audio.onended = () => {
        if (this.timerInterval) {
          clearInterval(this.timerInterval);
          this.timerInterval = null;
          this.recordingTime = 0;
          this.isPlaying = false;
        }
      };

      this.audio.onplay = () => {
        
      }

      this.audio.play();
    }
  }

  uploadAudio() {
    if (this.audioBlob) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64data = reader.result as string; // Base64-encoded string

        // Prepare the payload
        const payload = {
          fileName: 'recording.'+this.mimeType,
          fileData: base64data.split(',')[1] // Remove the Base64 prefix
        };

        // Send the Base64 data to the server
        this.http.post('https://your-backend-endpoint.com/records', payload).subscribe({
          next: (response) => {
            console.log('Audio uploaded successfully'+ response);
            //alert('Audio uploaded successfully!');
          },
          error: (error) => {
            this.toastr.error('Error uploading audio');
          }
        });
      };

      // Read the Blob as a Base64 string
      reader.readAsDataURL(this.audioBlob);
    } else {
      alert('No audio to upload.');
    }
    //some comment to check commit
  }
}
